import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { usePageStore } from "@/hooks/usePageStore";
import { Box, CardContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "@/utils/theme";
import {
  useDeleteOrganization,
  useGetOrganizations,
} from "@/hooks/management/useOrganizations.manage";
import Card from "@mui/material/Card";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import { SkeletonHeaderButtons } from "@/ui/header/skeleton-button";
import Button from "@mui/material/Button";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import Chip from "@mui/material/Chip";
import ActionMenu from "@/components/ActionMenu";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OrganizationAddModal from "@/ui/modals/organizations/OrganizationAddModal";
import OrganizationUpdateModal from "@/ui/modals/organizations/OrganizationUpdateModal";
import { Organization } from "@/domain/organizations.interface";
import AlertDialog from "@/components/AlertDialog";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import LightTooltip from "@/components/LightTooltip";
import SearchBox from "@/components/SearchBox";
import useDebounce from "@/hooks/useDebounce";
import { useMaxOuSetting } from "@/hooks/useSetting";
import { AlertType, useAlertModal } from "@/hooks/useAlertModal";

const OrganizationsPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { t } = useTranslation("organization");
  const { showSnackBar, showSimpleAlert } = useFeedBack();
  const { userInfo } = useUserStore();
  const { alertState, closeAlert, confirmAction } = useAlertModal();

  const maxOuSetting = useMaxOuSetting();

  const createAuditLog = useCreateAuditlog();
  const deleteOrganization = useDeleteOrganization();
  const auditLogViewCalledRef = React.useRef(false);

  useEffect(() => {
    setPageTitle(t("title"));
  }, []);

  // TODO: Implement the organizations list filters on Backend
  const [filters, setFilters] = useState({
    search: "",
    sortField: "name",
    sortOrder: 1,
    pageNumber: 0,
    pageSize: 10,
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<{
    open: boolean;
    organization: Organization | null;
  }>({
    open: false,
    organization: null,
  });

  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization>();

  const searchDebounce = useDebounce({ value: filters.search, delay: 500 });

  const { data, isLoadingOrganizations, refetchOrganizations } =
    useGetOrganizations(filters);

  const headCells = useMemo(() => {
    const baseHeadCells: HeadCell[] = [
      {
        id: "name",
        isSortable: true,
        disablePadding: true,
        label: t("header.name"),
        align: "left",
        width: "320px",
      },
      {
        id: "groupLabel",
        isSortable: false,
        disablePadding: false,
        label: t("header.group-label"),
        align: "left",
        width: "200px",
      },
      {
        id: "actions",
        isSortable: false,
        disablePadding: false,
        label: t("header.actions"),
        align: "right",
        width: "200px",
      },
    ];

    return baseHeadCells;
  }, [data]);

  const rows = useMemo(() => {
    if (data && data.organizations.length === 0) return [];

    const organizations = data?.organizations;

    return (
      organizations?.map((organization) => {
        const actionMenu = [
          {
            value: "Edit",
            onClick: () => {
              setSelectedOrganization(organization);
              setShowUpdateModal(true);
            },
            icon: <EditIcon fontSize="small" />,
          },
          {
            value: "Delete",
            onClick: () => {
              setShowDeleteModal({ open: true, organization });
            },
            icon: <DeleteOutlineIcon fontSize="small" />,
            sx: {
              color: theme.palette.error.main,
            },
          },
        ];

        const record: Record<string, ReactNode> = {
          name: <div>{organization.name}</div>,
          groupLabel: (
            <div className="flex gap-1">
              {organization.groupLabels.map((groupLabel) => (
                <Chip
                  label={<div className="text-xs">{groupLabel.name}</div>}
                />
              ))}
            </div>
          ),
          actions: <ActionMenu id={organization._id} options={actionMenu} />,
        };

        const row = createDataRow(organization._id, record);

        return row;
      }) || []
    );
  }, [data]);

  const handleDelete = async () => {
    const orgName = showDeleteModal.organization?.name;

    deleteOrganization.mutate(showDeleteModal.organization?._id as string, {
      onSuccess: () => {
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ORGANIZATION,
          option: OPTIONS.ORGANIZATION_DELETE,
          actionCode: ACTION_CODE.WEB_OU_DELETE,
          action: "When deleting",
          detail: `Delete OU ${orgName}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: "66e0600b516d00801590a231",
          name: userInfo?.firstName + " " + userInfo?.lastName,
        });

        refetchOrganizations();
        setShowDeleteModal({ open: false, organization: null });
        showSnackBar(t("messages.delete-success"), "success");
      },
      onError: (error: any) => {
        setShowDeleteModal({ open: false, organization: null });
        showSnackBar(
          error?.response?.data?.message || "Failed to delete organization",
          "error",
        );
      },
    });
  };

  const handleAddClick = () => {
    if (data?.totalCount && data?.totalCount > maxOuSetting) {
      showSimpleAlert(
        t("messages.max-number-organizations", { num: maxOuSetting }),
      );
      return;
    }
    setShowAddModal(true);
  };

  useEffect(() => {
    refetchOrganizations();
  }, [
    filters.sortField,
    filters.sortOrder,
    filters.pageNumber,
    filters.pageSize,
    searchDebounce,
  ]);

  useEffect(() => {
    if (!auditLogViewCalledRef.current) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ORGANIZATION,
        option: OPTIONS.ORGANIZATION_LIST,
        actionCode: ACTION_CODE.WEB_OU_LIST_VIEW,
        action: "When entering",
        detail: "Viewed the list of OU",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: "66e0600b516d00801590a231",
        name: userInfo?.firstName + " " + userInfo?.lastName,
      });
      auditLogViewCalledRef.current = true;
    }
  }, []);

  return (
    <Box>
      <Breadcrumbs
        items={[
          {
            label: t("page-title"),
            href: "/organizations",
            isLink: true,
          },
        ]}
      />

      <Box
        mt={1}
        mb={4}
        sx={{
          width: "100%",
        }}
        display="flex"
        justifyContent="space-between"
        alignItems={"center"}
      >
        <Typography
          color={theme.palette.textMain.dark2}
          variant="headlineSmall"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          {t("page-title")}

          <LightTooltip title={t("messages.information")} arrow theme={theme}>
            <InfoOutlinedIcon
              sx={{
                cursor: "pointer",
              }}
            />
          </LightTooltip>
        </Typography>

        <Box display="flex" gap="0.5rem">
          {isLoadingOrganizations ? (
            <SkeletonHeaderButtons />
          ) : (
            <>
              <Button
                size={"medium"}
                onClick={handleAddClick}
                variant="contained"
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  textAlign: "center",
                  height: "40px",
                  backgroundColor: theme.palette.primary.dark,
                  color: theme.palette.textMain.light,
                }}
                startIcon={<AddIcon />}
              >
                {t("buttons.add")}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Card>
        <CardContent>
          {/* Filters - Search */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 2,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            { 
            <Box
              sx={{
                flex: { xs: "none", md: "0 0 50%" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <SearchBox
                theme={theme}
                onChange={(value) => {
                  setFilters((filters) => ({
                    ...filters,
                    search: value,
                    pageNumber: 0,
                  }));
                }}
                value={filters.search}
                placeHolder="Search..."
              />
            </Box>
            }
          </Box>
          <DataTable
            data={rows}
            headCells={headCells}
            order={filters.sortOrder === 1 ? "asc" : "desc"}
            orderBy={filters.sortField}
            rowsPerPage={filters.pageSize}
            page={filters.pageNumber}
            total={data?.totalCount}
            isLoading={isLoadingOrganizations}
            onChangePage={(event, newPage) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: newPage,
              }));
            }}
            onChangeSize={(event) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                pageSize: Number(event.target.value),
              }));
            }}
            onChangeSort={(order, orderBy) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                sortField: orderBy as string,
                sortOrder: order === "asc" ? 1 : -1,
              }));
            }}
          />
        </CardContent>
      </Card>

      {showAddModal ? (
        <OrganizationAddModal
          open={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            refetchOrganizations();
          }}
        />
      ) : null}

      {showUpdateModal ? (
        <OrganizationUpdateModal
          open={showUpdateModal}
          organization={selectedOrganization as Organization}
          onClose={() => {
            setShowUpdateModal(false);
            refetchOrganizations();
          }}
        />
      ) : null}

      {showDeleteModal.open && (
        <AlertDialog
          open={showDeleteModal.open}
          title={t("messages.delete-title")}
          description={t("messages.delete-confirm")}
          onCancel={() => {
            setShowDeleteModal({ open: false, organization: null });
          }}
          onConfirm={handleDelete}
          confirmText={t("buttons.confirm")}
          cancelText={t("buttons.cancel")}
        />
      )}

      {alertState.open && (
        <AlertDialog
          open={alertState.open}
          onCancel={
            alertState.alertType === AlertType.CONFIRMATION
              ? closeAlert
              : closeAlert
          }
          onConfirm={confirmAction}
          title={alertState.title}
          description={alertState.message}
          confirmText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Confirm"
              : "Accept"
          }
          cancelText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Cancel"
              : undefined
          }
        />
      )}
    </Box>
  );
};

export default OrganizationsPage;
